"use client";

import React from "react";
import { Play, Settings2, ArrowRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const ritualSteps = [{
  id: "inspection",
  title: "01 Inspection",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658340/img/foundation_inspection_blueprint.png",
  description: "A comprehensive site visit to identify structural distress. We analyze crack patterns, soil settlement, and drainage issues using high-precision laser levels and digital manometers to establish a baseline for repair.",
  equipment: "Digital Manometers, Laser Levels"
}, {
  id: "evaluation",
  title: "02 Evaluation",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658340/img/structural_evaluation_analysis.png",
  description: "Mohammed Saleem, P.E., personally reviews the data to determine the root cause of failure. We differentiate between cosmetic settling and structural instability to ensure the most cost-effective engineering solution.",
  equipment: "Soil Boring Logs, Structural Modeling"
}, {
  id: "engineering",
  title: "03 Engineering Plans",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658340/img/engineering_plans_drafting.png",
  description: "Detailed structural blueprints are drafted to meet California or Utah building codes. These plans specify pier placement, reinforcement details, and load-bearing calculations required for permit approval.",
  equipment: "AutoCAD, Structural Load Software"
}, {
  id: "correction",
  title: "04 Final Correction",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658340/img/foundation_correction_execution.png",
  description: "The implementation phase where our engineered designs are executed. We provide third-party oversight to ensure contractors adhere strictly to the structural specifications for a permanent foundation fix.",
  equipment: "Hydraulic Jacks, Steel Pier Systems"
}];
export default function TheBrewingRitual() {
  return <section data-section-id="226223" id="the-foundation-repair-journey" className="bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 overflow-hidden relative">
      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes slowFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slow-fade {
          animation: slowFadeIn 1s ease-out forwards;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `
    }} />

      <div className="max-w-7xl mx-auto relative z-10">
        <header className="text-center mb-16 md:mb-24">
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 text-primary tracking-tighter">
            The Foundation Repair Journey
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg md:text-xl font-default leading-relaxed">
            A systematic, engineering-first approach to structural restoration. From initial site inspection to final certification of stability.
          </p>
        </header>

        <Tabs defaultValue="inspection" className="w-full flex flex-col items-center">
          <div className="w-full max-w-4xl border-b border-border mb-12 md:mb-16">
            <TabsList className="bg-transparent h-auto p-0 w-full justify-start md:justify-center overflow-x-auto flex-nowrap hide-scrollbar gap-8 md:gap-16">
              {ritualSteps.map((step, i) => <TabsTrigger key={step.id} value={step.id} data-index={i} className="rounded-none border-b-2 border-transparent px-0 py-4 text-sm md:text-base font-mono tracking-widest uppercase text-muted-foreground/60 data-[state=active]:border-ring data-[state=active]:text-ring data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-primary transition-colors whitespace-nowrap">
                  {step.title}
                </TabsTrigger>)}
            </TabsList>
          </div>

          <div className="w-full min-h-[500px]">
            {ritualSteps.map((step, i) => <TabsContent key={step.id} value={step.id} data-index={i} className="w-full mt-0 outline-none">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center animate-slow-fade">
                  
                  <div className="lg:col-span-7 relative aspect-[4/3] md:aspect-video lg:aspect-[4/3] rounded-sm overflow-hidden bg-secondary group shadow-lg shadow-primary/10">
                    <img src={step.image} alt={`${step.title} technical phase`} className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-105 grayscale hover:grayscale-0" />
                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center transition-colors duration-700 group-hover:bg-transparent">
                      <Button variant="ghost" size="icon" className="size-20 rounded-none bg-background/90 backdrop-blur-sm text-primary shadow-xl hover:scale-105 transition-transform duration-500 border border-border hover:bg-background" aria-label={`View ${step.title} details`}>
                        <Play className="size-8 ml-1 fill-ring/20 text-ring" strokeWidth={1.5} />
                      </Button>
                    </div>
                    <div className="absolute top-4 left-4 w-4 h-4 border-t border-l border-ring/50"></div>
                    <div className="absolute bottom-4 right-4 w-4 h-4 border-b border-r border-ring/50"></div>
                  </div>

                  <div className="lg:col-span-5 flex flex-col gap-8">
                    <div>
                      <div className="inline-flex items-center gap-2 text-ring text-xs font-mono uppercase tracking-widest mb-4">
                        <Settings2 className="size-4" />
                        Engineering Protocol
                      </div>
                      <h3 className="font-default text-3xl md:text-4xl font-bold text-primary mb-6 tracking-tight">
                        {step.title}
                      </h3>
                      <p className="text-muted-foreground text-lg leading-relaxed font-default">
                        {step.description}
                      </p>
                    </div>

                    <Card className="bg-secondary border-border shadow-none rounded-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-xs font-mono text-muted-foreground uppercase tracking-widest">
                          Technical Instrumentation
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="font-mono text-lg md:text-xl font-medium text-primary">
                          {step.equipment}
                        </p>
                      </CardContent>
                    </Card>

                    <div className="pt-4">
                      <Button asChild variant="link" className="p-0 h-auto text-ring hover:text-ring/80 font-default text-lg font-semibold tracking-tight">
                        <Link to="/ExpertisePage">
                          View Technical Standards <ArrowRight className="ml-2 size-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>

                </div>
              </TabsContent>)}
          </div>
        </Tabs>
      </div>
    </section>;
}